import { useCallback, useState } from "react";
import {
  AlertTriangle,
  Cloud,
  KeyRound,
  Layers,
  Link2,
  Loader2,
  RotateCcw,
  Save,
  Unlink,
} from "lucide-react";

import { useServiceLabel } from "@/api/local/labels";
import {
  PROVIDER_KINDS,
  PROVIDER_META,
  getEffectiveServiceEntry,
  serviceProvidersMode,
  useClearServiceCredentials,
  useCloudRegions,
  useDetachServiceProviders,
  useInheritServiceProviders,
  useProviders,
  useProvidersStatus,
  useUpdateServiceEntry,
  type ProviderEntry,
  type ProviderEntryPatch,
  type ProviderKind,
} from "@/api/local/providers";
import type { Service } from "@/api/types";
import { useConfirm } from "@/components/ConfirmDialog";
import { Pill } from "@/components/Pill";
import { ProviderConfigForm } from "@/components/ProviderConfigForm";
import { Section } from "@/components/Section";
import { ToggleSwitch } from "@/components/ToggleSwitch";
import { VariantsRegionsCell } from "@/components/VariantsRegionsCell";
import {
  overlayValues,
  useProvidersDraft,
} from "@/components/useProvidersDraft";

interface Props {
  service: Service;
}

/**
 * Service-scoped provider overrides.
 *
 * Mirrors the Workload page's inherit/own pattern: the entire scope is
 * either ``inherit`` (no service-scoped rows; the global catalog is
 * read-only displayed) or ``own`` (one row per kind, copied from the
 * parent on the first ``override per-service`` click). There is no
 * per-row inherit toggle — the row's existence IS the mode flag, the
 * same way ``Pool.workload IS NULL ⇔ inherit`` works for Workload.
 *
 * Edits to enabled / regions / variants on an own scope are buffered
 * in a page-local ``useProvidersDraft`` and persisted in one shot via
 * the section's save button.
 */
export function ServiceProviders({ service }: Props) {
  const doc = useProviders();
  const { isLoading } = useProvidersStatus();
  const svcState = doc.byService.find((s) => s.serviceId === service.id);
  const updateServiceEntry = useUpdateServiceEntry();
  const clearServiceCredentials = useClearServiceCredentials();
  const detach = useDetachServiceProviders();
  const inheritAgain = useInheritServiceProviders();
  const [editing, setEditing] = useState<ProviderKind | null>(null);
  const [busy, setBusy] = useState<"detach" | "inherit" | null>(null);
  const displayName = useServiceLabel(service.id, service.name);
  const confirm = useConfirm();

  const mode = serviceProvidersMode(doc, service.id);
  const isOwn = mode === "own";

  const commit = useCallback(
    async (kind: ProviderKind, patch: ProviderEntryPatch) => {
      await updateServiceEntry(service.id, kind, patch);
    },
    [service.id, updateServiceEntry],
  );
  const draft = useProvidersDraft(commit);

  const handleDetach = async () => {
    setBusy("detach");
    try {
      await detach(service.id);
    } finally {
      setBusy(null);
    }
  };

  const handleInherit = async () => {
    const ok = await confirm({
      title: "Revert to inherit Global providers?",
      tone: "warn",
      confirmLabel: "Revert to inherit",
      description:
        "Service-scoped provider rows (credentials, region allow-lists, enabled toggles) will be deleted. Pool overrides nested under this service are kept.",
    });
    if (!ok) return;
    draft.discard();
    setBusy("inherit");
    try {
      await inheritAgain(service.id);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <Section
        title={`${displayName} · provider overrides`}
        icon={<Cloud />}
        flush
        count={
          <span style={{ display: "inline-flex", gap: 6 }}>
            <Pill tone={isOwn ? "warn" : "neutral"}>
              {isOwn ? "OWN" : "INHERIT"}
            </Pill>
            {isOwn && draft.isDirty && (
              <Pill tone="warn">
                {draft.dirtyKinds.length} unsaved
              </Pill>
            )}
          </span>
        }
        actions={
          <ScopeActions
            mode={mode}
            busy={busy}
            draft={draft}
            onDetach={handleDetach}
            onInherit={handleInherit}
          />
        }
      >
        <ScopeBanner mode={mode} displayName={displayName} />

        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Provider</th>
                <th>Enabled</th>
                <th>Regions</th>
                <th>Credential</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {PROVIDER_KINDS.map((kind) => {
                const local = svcState?.entries[kind];
                const d = draft.drafts[kind];
                const effective = getEffectiveServiceEntry(
                  doc,
                  service.id,
                  kind,
                );
                // In own mode the row's own values back the editor; in
                // inherit mode we display the parent's effective entry
                // read-only.
                const baseEntry: ProviderEntry | undefined = isOwn
                  ? (local ?? effective)
                  : effective;
                const enabledEffective =
                  d?.enabled ?? (baseEntry?.enabled ?? false);
                const variantSourceEntry: ProviderEntry | undefined = baseEntry
                  ? { ...baseEntry, values: overlayValues(baseEntry, d) }
                  : d?.values
                    ? {
                        id: null,
                        enabled: enabledEffective,
                        values: { ...d.values },
                        secret_keys: [],
                        key_last4: null,
                      }
                    : undefined;
                return (
                  <ProviderRow
                    key={kind}
                    kind={kind}
                    loading={isLoading}
                    readOnly={!isOwn}
                    enabled={enabledEffective}
                    keyLast4={effective.key_last4 ?? null}
                    inheritedFromParent={!isOwn}
                    dirty={d !== undefined}
                    variantSourceEntry={variantSourceEntry}
                    onConfigure={() => setEditing(kind)}
                    onToggleEnabled={(v) => draft.setEnabled(kind, v)}
                    onChangeVariant={(variantKey, next) =>
                      draft.setVariant(kind, variantKey, next)
                    }
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>

      {editing && isOwn && (
        <ProviderConfigForm
          open={true}
          onClose={() => setEditing(null)}
          meta={PROVIDER_META[editing]}
          scopeLabel={`service: ${displayName}`}
          entry={svcState?.entries[editing]}
          onSave={(patch) => updateServiceEntry(service.id, editing, patch)}
          onClear={(opts) =>
            clearServiceCredentials(service.id, editing, opts)
          }
        />
      )}
    </>
  );
}

function ScopeBanner({
  mode,
  displayName,
}: {
  mode: "inherit" | "own";
  displayName: string;
}) {
  if (mode === "inherit") {
    return (
      <div className="flash flash--info" style={{ margin: "12px 18px 0" }}>
        <Layers
          style={{
            width: 14,
            height: 14,
            verticalAlign: "middle",
            marginRight: 6,
          }}
        />
        This service is <strong>inheriting</strong> the provider catalog
        from <code>Global</code>. Replicas dispatched under{" "}
        <strong>{displayName}</strong> use whatever credentials, regions,
        and enabled toggles are set globally. Click{" "}
        <strong>override per-service</strong> to detach: the controller
        copies every globally-configured kind into a service-scoped row
        (credentials are re-encrypted under the new row's key) and
        unlocks the editor below.
      </div>
    );
  }
  return (
    <div className="flash" style={{ margin: "12px 18px 0" }}>
      <Unlink
        style={{
          width: 14,
          height: 14,
          verticalAlign: "middle",
          marginRight: 6,
        }}
      />
      This service <strong>owns</strong> its provider configuration —
      Global edits will <em>not</em> flow through here automatically.
      Use <strong>inherit again</strong> to drop every service-scoped
      row and follow <code>Global</code> once more. Pool-level overrides
      under this service are kept either way.
    </div>
  );
}

interface ScopeActionsProps {
  mode: "inherit" | "own";
  busy: "detach" | "inherit" | null;
  draft: ReturnType<typeof useProvidersDraft>;
  onDetach: () => void;
  onInherit: () => void;
}

function ScopeActions({
  mode,
  busy,
  draft,
  onDetach,
  onInherit,
}: ScopeActionsProps) {
  if (mode === "inherit") {
    return (
      <button
        type="button"
        className="btn btn--primary"
        onClick={onDetach}
        disabled={busy !== null}
        title="Detach this service from the Global provider catalog — start editing service-scoped rows"
      >
        {busy === "detach" ? <Loader2 /> : <Unlink />}
        override per-service
      </button>
    );
  }
  const { isDirty, saving, error, save, discard } = draft;
  return (
    <div className="toolbar" style={{ gap: 6, alignItems: "center" }}>
      {error && (
        <span
          className="flash"
          style={{ padding: "2px 8px", fontSize: 12 }}
          title={error}
        >
          <AlertTriangle
            style={{
              width: 12,
              height: 12,
              verticalAlign: "-2px",
              marginRight: 4,
            }}
          />
          save failed
        </span>
      )}
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        onClick={discard}
        disabled={!isDirty || saving || busy !== null}
        title="Drop pending edits and revert"
      >
        <RotateCcw />
        discard
      </button>
      <button
        type="button"
        className="btn btn--primary btn--sm"
        onClick={() => void save()}
        disabled={!isDirty || saving || busy !== null}
        title={isDirty ? "Persist every pending row edit" : "no pending changes"}
      >
        <Save />
        {saving ? "saving…" : "save"}
      </button>
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        onClick={onInherit}
        disabled={busy !== null}
        title="Drop service-scoped provider rows and inherit Global again"
      >
        {busy === "inherit" ? <Loader2 /> : <Link2 />}
        inherit again
      </button>
    </div>
  );
}

interface RowProps {
  kind: ProviderKind;
  loading: boolean;
  readOnly: boolean;
  enabled: boolean;
  keyLast4: string | null;
  inheritedFromParent: boolean;
  dirty: boolean;
  variantSourceEntry: ProviderEntry | undefined;
  onConfigure: () => void;
  onToggleEnabled: (next: boolean) => void;
  onChangeVariant: (
    variantKey: string,
    next: { regions: string[]; enabled: boolean },
  ) => void;
}

function ProviderRow({
  kind,
  loading,
  readOnly,
  enabled,
  keyLast4,
  inheritedFromParent,
  dirty,
  variantSourceEntry,
  onConfigure,
  onToggleEnabled,
  onChangeVariant,
}: RowProps) {
  const meta = PROVIDER_META[kind];
  const regionSuggestions = useCloudRegions(
    meta.supportsRegions ? kind : undefined,
  );

  return (
    <tr className={dirty ? "row--dirty" : undefined}>
      <td>
        <strong>{meta.label}</strong>
        <div className="muted" style={{ fontSize: 11 }}>
          {meta.hint}
        </div>
        {dirty && (
          <div className="muted" style={{ fontSize: 10.5, marginTop: 2 }}>
            unsaved
          </div>
        )}
      </td>
      <td>
        <ToggleSwitch
          disabled={readOnly || loading}
          checked={enabled}
          onChange={onToggleEnabled}
          ariaLabel={`Enable ${meta.label} for this service`}
        />
      </td>
      <td>
        {meta.supportsRegions ? (
          <VariantsRegionsCell
            kind={kind}
            entry={variantSourceEntry}
            suggestions={regionSuggestions}
            readOnly={readOnly || loading}
            onChangeVariant={onChangeVariant}
          />
        ) : (
          <span className="muted" style={{ fontSize: 11.5 }}>
            n/a
          </span>
        )}
      </td>
      <td>
        {loading ? (
          <Pill tone="info" pulse title="loading provider state…">
            loading…
          </Pill>
        ) : inheritedFromParent ? (
          <Pill tone={keyLast4 ? "ok" : "warn"}>
            {keyLast4 ? `…${keyLast4} (inherited)` : "no key (inherited)"}
          </Pill>
        ) : keyLast4 ? (
          <Pill tone="ok">…{keyLast4}</Pill>
        ) : (
          <Pill tone="warn">no key</Pill>
        )}
      </td>
      <td className="actions">
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          disabled={readOnly || loading}
          onClick={onConfigure}
        >
          <KeyRound />
          configure
        </button>
      </td>
    </tr>
  );
}
